import { supabase } from "@/integrations/supabase/client";

// Types
export interface OnboardingStepConfig {
  id: string;
  step_key: string;
  step_order: number;
  display_name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OnboardingRoleAccess {
  id: string;
  step_key: string;
  role: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface OnboardingStepWithAccess extends OnboardingStepConfig {
  roleAccess: OnboardingRoleAccess[];
}

// ============================================
// Step Config Operations
// ============================================

/**
 * Get all onboarding step configurations ordered by step_order
 */
export const getOnboardingStepConfigs = async (): Promise<{
  success: boolean;
  data?: OnboardingStepConfig[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('onboarding_step_config')
      .select('*')
      .order('step_order', { ascending: true });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching onboarding step configs:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Get active onboarding steps for a specific user based on their roles
 */
export const getActiveStepsForUser = async (userId: string): Promise<{
  success: boolean;
  data?: OnboardingStepConfig[];
  error?: string;
}> => {
  try {
    // Get user's roles
    const { data: userRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    if (rolesError) throw rolesError;

    const roles = userRoles?.map(r => r.role) || [];

    // If user has no roles, default to 'customer'
    if (roles.length === 0) {
      roles.push('customer');
    }

    // Get all active steps
    const { data: steps, error: stepsError } = await supabase
      .from('onboarding_step_config')
      .select('*')
      .eq('is_active', true)
      .order('step_order', { ascending: true });

    if (stepsError) throw stepsError;

    // Get role access for user's roles
    const { data: roleAccess, error: accessError } = await supabase
      .from('onboarding_role_access')
      .select('*')
      .in('role', roles)
      .eq('is_enabled', true);

    if (accessError) throw accessError;

    // Filter steps that have access for at least one of the user's roles
    const accessibleStepKeys = new Set(roleAccess?.map(ra => ra.step_key) || []);
    const filteredSteps = steps?.filter(step => accessibleStepKeys.has(step.step_key)) || [];

    return { success: true, data: filteredSteps };
  } catch (error) {
    console.error('Error fetching active steps for user:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Update a step's active status (admin only)
 */
export const updateStepActiveStatus = async (
  stepKey: string,
  isActive: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('onboarding_step_config')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('step_key', stepKey);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating step active status:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// ============================================
// Role Access Operations
// ============================================

/**
 * Get all role access configurations
 */
export const getOnboardingRoleAccess = async (): Promise<{
  success: boolean;
  data?: OnboardingRoleAccess[];
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('onboarding_role_access')
      .select('*');

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching onboarding role access:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Update role access for a step (admin only)
 */
export const updateRoleAccess = async (
  stepKey: string,
  role: string,
  isEnabled: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Check if record exists
    const { data: existing } = await supabase
      .from('onboarding_role_access')
      .select('id')
      .eq('step_key', stepKey)
      .eq('role', role as any)
      .maybeSingle();

    if (existing) {
      // Update existing
      const { error } = await supabase
        .from('onboarding_role_access')
        .update({ is_enabled: isEnabled, updated_at: new Date().toISOString() })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      // Insert new
      const { error } = await supabase
        .from('onboarding_role_access')
        .insert({
          step_key: stepKey,
          role: role as any,
          is_enabled: isEnabled,
        });
      if (error) throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating role access:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// ============================================
// User Onboarding Status
// ============================================

/**
 * Check if user has completed onboarding
 */
export const hasCompletedOnboarding = async (userId: string): Promise<{
  success: boolean;
  completed: boolean;
  completedAt?: string | null;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('onboarding_completed_at')
      .eq('id', userId)
      .single();

    if (error) throw error;
    
    return {
      success: true,
      completed: data?.onboarding_completed_at !== null,
      completedAt: data?.onboarding_completed_at,
    };
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return { success: false, completed: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Mark onboarding as completed for a user
 */
export const markOnboardingCompleted = async (userId: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ onboarding_completed_at: new Date().toISOString() })
      .eq('id', userId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error marking onboarding completed:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

/**
 * Reset onboarding for a user (admin function for testing)
 */
export const resetOnboarding = async (userId: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase
      .from('users')
      .update({ onboarding_completed_at: null })
      .eq('id', userId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error resetting onboarding:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};

// ============================================
// Combined Data for Admin
// ============================================

/**
 * Get all steps with their role access (admin view)
 */
export const getStepsWithRoleAccess = async (): Promise<{
  success: boolean;
  data?: OnboardingStepWithAccess[];
  error?: string;
}> => {
  try {
    const [stepsResult, accessResult] = await Promise.all([
      getOnboardingStepConfigs(),
      getOnboardingRoleAccess(),
    ]);

    if (!stepsResult.success) throw new Error(stepsResult.error);
    if (!accessResult.success) throw new Error(accessResult.error);

    const steps = stepsResult.data || [];
    const access = accessResult.data || [];

    // Group role access by step_key
    const accessByStep = access.reduce((acc, ra) => {
      if (!acc[ra.step_key]) acc[ra.step_key] = [];
      acc[ra.step_key].push(ra);
      return acc;
    }, {} as Record<string, OnboardingRoleAccess[]>);

    // Combine steps with their role access
    const stepsWithAccess: OnboardingStepWithAccess[] = steps.map(step => ({
      ...step,
      roleAccess: accessByStep[step.step_key] || [],
    }));

    return { success: true, data: stepsWithAccess };
  } catch (error) {
    console.error('Error fetching steps with role access:', error);
    return { success: false, error: error instanceof Error ? error.message : String(error) };
  }
};
